'use client';

import { forwardRef } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { bannerClickEvent, shopReferral } from '@/lib/gtm';

import { LocalImage } from '@/components/atoms/LocalImage';
import H4 from '@/components/atoms/Typography/H4';
import type { SectionProps } from '@/components/layout/Section';
import Section from '@/components/layout/Section';

import type { SlideDataType } from './LogosSliderSection.types';

export interface LogosSliderSectionProps extends SectionProps {
    title: string;
    sliderData: SlideDataType[];
}

const LogosSliderSection = forwardRef<HTMLDivElement, LogosSliderSectionProps>(
    ({ className, title, sliderData, ...props }, ref) => (
        <Section
            className={className}
            containerClassName="max-w-full"
            ref={ref}
            {...props}
        >
            <H4 className="heading-spaces mx-auto w-full max-w-[1168px] text-center lg:text-left">
                {title}
            </H4>

            <div className="relative z-10 -mx-6 max-w-none bg-background-secondary py-6 md:-mx-12 md:py-12">
                <Swiper
                    className="!flex w-screen !overflow-visible"
                    spaceBetween={4}
                    slidesPerView="auto"
                    centerInsufficientSlides
                    modules={[Navigation, Autoplay]}
                    loop
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    grabCursor
                >
                    {sliderData.map(slideData => {
                        const gtmSlideEvent = () => {
                            shopReferral(slideData.name, 'Strona Główna Hero');
                        };

                        const gtmLinkEvent = () => {
                            bannerClickEvent(slideData.name, 'Horizontal');
                        };

                        return (
                            <SwiperSlide
                                className="!flex min-h-20 !w-52 flex-col items-center justify-center rounded-lg bg-background p-0"
                                key={slideData.name}
                                onClick={gtmSlideEvent}
                            >
                                <a
                                    href={slideData.href}
                                    target="_blank"
                                    onClick={gtmLinkEvent}
                                    className="relative flex min-h-20 w-full items-center justify-center p-1 opacity-60 transition-all duration-500 hover:opacity-100 focus:outline-2 focus:outline-offset-2 hover:[&>img]:grayscale-0"
                                >
                                    <LocalImage
                                        className="m-auto max-h-10 max-w-32 object-contain grayscale"
                                        fill
                                        {...slideData.image}
                                    />
                                </a>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
        </Section>
    ),
);

export default LogosSliderSection;
